import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import MapView from '@arcgis/core/views/MapView';
import WebMap from '@arcgis/core/WebMap';
import type { InitialView } from './constants';

type UseLocalArcgisMapViewArgs = {
  isActive: boolean;
  containerRef: RefObject<HTMLDivElement>;
  basemap: string | __esri.Basemap;
  initialView: InitialView;
  onMapReady?: (mapView: MapView) => void;
};

export const useLocalArcgisMapView = ({
  isActive,
  containerRef,
  basemap,
  initialView,
  onMapReady,
}: UseLocalArcgisMapViewArgs) => {
  const [mapView, setMapView] = useState<MapView | null>(null);
  const onMapReadyRef = useRef(onMapReady);
  const [centerLongitude, centerLatitude] = initialView.center;
  const paddingLeft = initialView.padding?.left ?? 0;
  const paddingTop = initialView.padding?.top ?? 0;
  const paddingRight = initialView.padding?.right ?? 0;
  const paddingBottom = initialView.padding?.bottom ?? 0;

  useLayoutEffect(() => {
    onMapReadyRef.current = onMapReady;
  }, [onMapReady]);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const webMap = new WebMap({ basemap });
    const nextMapView = new MapView({
      map: webMap,
      container: containerRef.current,
      center: [centerLongitude, centerLatitude],
      zoom: initialView.zoom,
      padding: {
        left: paddingLeft,
        top: paddingTop,
        right: paddingRight,
        bottom: paddingBottom,
      },
    });

    let isDisposed = false;
    nextMapView.when(() => {
      if (isDisposed) return;
      setMapView(nextMapView);
      onMapReadyRef.current?.(nextMapView);
    });

    return () => {
      isDisposed = true;
      setMapView(null);
      nextMapView.destroy();
      webMap.destroy();
    };
  }, [
    basemap,
    centerLatitude,
    centerLongitude,
    containerRef,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    initialView.zoom,
    isActive,
  ]);

  return { mapView };
};
